import { useEffect, useState } from 'react';
import { Card, Modal, FormField, TextInput, TextArea, EmptyState, Icon, PageHeader } from '@/components/ui';
import { agentApi } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';

interface Task {
  id: number;
  title: string;
  description?: string;
  due_at?: string;
  status: 'open' | 'done';
}

export function Tasks() {
  const [today, setToday] = useState<Task[]>([]);
  const [upcoming, setUpcoming] = useState<Task[]>([]);
  const [done, setDone] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_at: '' });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    const data = await agentApi.tasks();
    setToday((data.today ?? []) as Task[]);
    setUpcoming((data.upcoming ?? []) as Task[]);
    setDone((data.done ?? []) as Task[]);
    setLoading(false);
  }

  async function complete(id: number) {
    try {
      await agentApi.updateTask(id, { status: 'done' });
      toast('הושלם', 'ok');
      void load();
    } catch {
      toast('שגיאה בעדכון המשימה', 'danger');
    }
  }

  async function remove(id: number) {
    try {
      await agentApi.deleteTask(id);
      toast('המשימה נמחקה', 'ok');
      void load();
    } catch {
      toast('שגיאה במחיקת המשימה', 'danger');
    }
  }

  async function add() {
    if (!form.title) return;
    try {
      await agentApi.createTask(form);
      setForm({ title: '', description: '', due_at: '' });
      setShowAdd(false);
      void load();
    } catch {
      toast('שגיאה ביצירת המשימה', 'danger');
    }
  }

  if (loading) return <Card>טוען…</Card>;

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="משימות" actions={<>
        <button type="button" className="btn primary" onClick={() => setShowAdd(true)}>
          + הוסף משימה
        </button>
      </>} />

      <Section title="היום" tasks={today} onComplete={complete} onDelete={remove} emptyMessage="אין משימות להיום" />
      <Section title="בקרוב" tasks={upcoming} onComplete={complete} onDelete={remove} emptyMessage="אין משימות עתידיות" />
      <Section title="הושלמו" tasks={done} onComplete={() => undefined} onDelete={remove} hideAction />

      <Modal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        title="משימה חדשה"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setShowAdd(false)}>
              ביטול
            </button>
            <button type="button" className="btn primary" onClick={add}>
              שמור
            </button>
          </>
        }
      >
        <div className="stack">
          <FormField label="כותרת" required>
            <TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </FormField>
          <FormField label="פרטים">
            <TextArea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </FormField>
          <FormField label="לתאריך ושעה">
            <TextInput
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}

function Section({
  title, tasks, onComplete, onDelete, emptyMessage, hideAction,
}: {
  title: string;
  tasks: Task[];
  onComplete: (id: number) => void;
  onDelete: (id: number) => void;
  emptyMessage?: string;
  hideAction?: boolean;
}) {
  return (
    <Card title={`${title} (${tasks.length})`}>
      {tasks.length === 0 ? (
        emptyMessage ? <EmptyState icon="ok" title={emptyMessage} /> : null
      ) : (
        <div className="stack" style={{ gap: 8 }}>
          {tasks.map((task) => (
            <div
              key={task.id}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                padding: 'var(--pad-row) var(--pad-card)',
                background: 'var(--surface-2)',
                borderRadius: 'var(--r-2)',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, textDecoration: task.status === 'done' ? 'line-through' : 'none' }}>
                  {task.title}
                </div>
                {task.description && (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{task.description}</div>
                )}
                {task.due_at && (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {new Date(task.due_at).toLocaleString('he-IL')}
                  </div>
                )}
              </div>
              {!hideAction && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => onComplete(task.id)}
                  aria-label="complete"
                >
                  <Icon name="check" size={14} />
                </button>
              )}
              <button
                type="button"
                className="btn"
                onClick={() => onDelete(task.id)}
                aria-label="delete"
                title="מחק"
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
